import React, { useEffect, useRef, useState } from "react";
import { Loader2, Search } from "lucide-react";

export interface SearchInputOption<T = any> {
  value: string;
  label: string;
  data: T;
}

interface SearchInputProps<T = any> {
  label?: string;
  placeholder?: string;
  /** Controlled display value, e.g. the currently selected item's label */
  value?: string;
  /** Called with the trimmed query after the debounce window; return matching options */
  onSearch: (query: string) => Promise<SearchInputOption<T>[]>;
  onSelect: (option: SearchInputOption<T>) => void;
  /** Custom row renderer for a result; falls back to plain label text */
  renderOption?: (option: SearchInputOption<T>) => React.ReactNode;
  /** Minimum characters typed before a search fires. Defaults to 0, so
   *  focusing an empty input shows an unfiltered "all items" list. */
  minChars?: number;
  debounceMs?: number;
  /** Clear the input after a selection instead of showing the picked label
   *  (useful for "add another" pickers like a product search) */
  clearOnSelect?: boolean;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

/**
 * Reusable debounced async search input: types → waits → calls onSearch →
 * shows results in a dropdown with an optional custom option renderer.
 * Used for supplier lookup (name/supplier ID) and product lookup
 * (name/SKU/HSN/product ID) — anywhere an id needs picking via API search
 * instead of preloading the full list into a <select>.
 */
function SearchInput<T = any>({
  label,
  placeholder = "Search...",
  value = "",
  onSearch,
  onSelect,
  renderOption,
  minChars = 0,
  debounceMs = 350,
  clearOnSelect = false,
  disabled = false,
  required = false,
  className = "",
}: SearchInputProps<T>) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<SearchInputOption<T>[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);

  // Stay in sync when the parent resets/clears the selected value externally
  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    // Don't fetch until the user actually opens the field — avoids firing
    // a request the moment the component mounts.
    if (!isOpen) return;

    const trimmed = query.trim();
    if (trimmed.length < minChars) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const requestId = ++requestIdRef.current;
    // Empty query (first open, nothing typed yet) shows the full list
    // instantly; anything typed after that debounces as normal.
    const delay = trimmed.length === 0 ? 0 : debounceMs;

    const timer = setTimeout(async () => {
      try {
        const res = await onSearch(trimmed);
        if (requestId === requestIdRef.current) setResults(res);
      } catch (err) {
        console.error("SearchInput: search failed", err);
        if (requestId === requestIdRef.current) setResults([]);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, delay);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, minChars, debounceMs, isOpen]);

  const handleSelect = (option: SearchInputOption<T>) => {
    onSelect(option);
    setQuery(clearOnSelect ? "" : option.label);
    setResults([]);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={`relative flex flex-col space-y-1 ${className}`}
    >
      {label && (
        <label className="text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-600">*</span>}
        </label>
      )}

      <div className="relative">
        <Search
          size={14}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          className="w-full border rounded-md pl-8 pr-8 py-2 text-sm border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
          placeholder={placeholder}
          value={query}
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
        />
        {loading && (
          <Loader2
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 animate-spin"
          />
        )}
      </div>

      {isOpen && query.trim().length >= minChars && (
        <div className="absolute top-full left-0 right-0 mt-1 z-20 bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto">
          {loading && results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">Searching...</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">No results found</div>
          ) : (
            results.map((option) => (
              <button
                type="button"
                key={option.value}
                onClick={() => handleSelect(option)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 border-b border-gray-100 last:border-b-0"
              >
                {renderOption ? renderOption(option) : option.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default SearchInput;
